import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Form, Button, Row, Col, Alert, Spinner, Card, Table, Accordion, Tabs, Tab } from 'react-bootstrap';
import axios from 'axios';
import { EventSpecification, FullEquipment, Consumable, Category, Subcategory } from '../interfaces';
import MessageModal from './MessageModal';
import EquipmentDetailModal from './EquipmentDetailModal';
// אייקונים
import { 
  faSave, 
  faTimes,
  faCog,
  faBox,
  faTruck
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { API_BASE_URL, buildApiUrl } from '../config/api';

const formatCasesLabel = (quantity: number, caseQty?: number) => {
  const cq = Number(caseQty || 1);
  if (!Number.isFinite(quantity) || quantity <= 0) return null;
  if (!Number.isFinite(cq) || cq <= 1) return null;

  const rawCases = quantity / cq;
  const isInt = Number.isInteger(rawCases);
  const casesText = isInt ? String(rawCases) : rawCases.toFixed(2);
  const noun = rawCases === 1 ? 'קייס' : 'קייסים';
  return `${casesText} ${noun}`;
};

// פונקציית עזר לקיבוץ ציוד מלאי לפי קטגוריה ותת-קטגוריה
const groupEquipment = (equipment: FullEquipment[]) => {
    const grouped: { [key: string]: { [key: string]: FullEquipment[] } } = {};
    equipment.forEach(item => {
        const category = item.category_name || 'Uncategorized';
        const subcategory = item.subcategory_name || 'Uncategorized';
        if (!grouped[category]) grouped[category] = {};
        if (!grouped[category][subcategory]) grouped[category][subcategory] = [];
        grouped[category][subcategory].push(item);
    });
    return grouped;
};

interface EventSpecificationFormProps {
  specificationToEdit?: EventSpecification | null;
  onSuccess: () => void;
  onCancel: () => void;
}

interface RentedItemData {
  name: string;
  category_id: number | '';
  subcategory_id: number | '';
  supplier_name: string;
  arrival_date: string;
  departure_date: string;
  quantity: number;
}

const EventSpecificationForm: React.FC<EventSpecificationFormProps> = ({ specificationToEdit, onSuccess, onCancel }) => {
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  
  const [allEquipment, setAllEquipment] = useState<FullEquipment[]>([]);
  const [allConsumables, setAllConsumables] = useState<Consumable[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [allSubcategories, setAllSubcategories] = useState<Subcategory[]>([]);
  
  const [equipmentQuantities, setEquipmentQuantities] = useState<{ [key: number]: string }>({});
  const [consumableQuantities, setConsumableQuantities] = useState<{ [key: number]: string }>({});
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [rentedItems, setRentedItems] = useState<RentedItemData[]>([]);
  
  const [addMode, setAddMode] = useState<'equipment' | 'consumable' | 'rented'>('equipment');
  
  const [loading, setLoading] = useState<boolean>(true);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const groupedInventory = useMemo(() => groupEquipment(allEquipment), [allEquipment]);
  
  // Modal לפרטי ציוד
  const [showEquipmentDetailModal, setShowEquipmentDetailModal] = useState<boolean>(false);
  const [selectedEquipmentForDetails, setSelectedEquipmentForDetails] = useState<FullEquipment | null>(null);

  useEffect(() => {
    const fetchDeps = async () => {
      try {
        const [equipRes, consumableRes, categoriesRes, subcategoriesRes] = await Promise.all([
          axios.get<FullEquipment[]>(buildApiUrl('inventory/equipment/')),
          axios.get<Consumable[]>(buildApiUrl('settings/consumables/')),
          axios.get<Category[]>(buildApiUrl('settings/categories/')),
          axios.get<Subcategory[]>(buildApiUrl('settings/subcategories/')),
        ]);
        setAllEquipment(equipRes.data);
        setAllConsumables(consumableRes.data);
        setAllCategories(categoriesRes.data);
        setAllSubcategories(subcategoriesRes.data);
      } catch (err) {
        setError("לא ניתן לטעון את רשימות הפריטים.");
      } finally {
        setLoading(false);
      }
    };
    fetchDeps();
  }, []);

  useEffect(() => {
    if (specificationToEdit) {
      setName(specificationToEdit.name);
      setDescription(specificationToEdit.description || '');
      
      const eqQuantities: { [key: number]: string } = {};
      specificationToEdit.items?.forEach(item => {
        if (item.equipment && item.quantity) {
          eqQuantities[item.equipment] = String(item.quantity);
        }
      });
      setEquipmentQuantities(eqQuantities);

      const conQuantities: { [key: number]: string } = {};
      specificationToEdit.consumables?.forEach(item => {
        if (item.consumable && item.quantity) {
          conQuantities[item.consumable] = String(item.quantity);
        }
      });
      setConsumableQuantities(conQuantities);

      // טעינת ציוד מושכר אם קיים
      if (specificationToEdit.rented_items) {
        const rentedItemsData: RentedItemData[] = specificationToEdit.rented_items.map(item => ({
          name: item.name || '',
          category_id: item.category_id || '',
          subcategory_id: item.subcategory_id || '',
          supplier_name: item.supplier_name || '',
          arrival_date: item.arrival_date || '',
          departure_date: item.departure_date || '',
          quantity: item.quantity || 1
        }));
        setRentedItems(rentedItemsData);
      }
    }
  }, [specificationToEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name) {
      setError("שם המפרט הוא שדה חובה.");
      return;
    }
    setSubmitLoading(true);

    const payload = {
      name,
      description,
      items_data: Object.entries(equipmentQuantities)
        .filter(([, qty]) => Number(qty) > 0)
        .map(([id, qty]) => ({ equipment: Number(id), quantity: Number(qty) })),
      consumables_data: Object.entries(consumableQuantities)
        .filter(([, qty]) => Number(qty) > 0)
        .map(([id, qty]) => ({ consumable: Number(id), quantity: Number(qty) })),
      rented_items_data: rentedItems
        .filter(item => item.name && item.category_id && item.supplier_name)
        .map(item => ({
          name: item.name,
          category_id: Number(item.category_id),
          subcategory_id: item.subcategory_id ? Number(item.subcategory_id) : null,
          supplier_name: item.supplier_name,
          arrival_date: item.arrival_date || null,
          departure_date: item.departure_date || null,
          quantity: item.quantity
        }))
    };

    try {
      if (specificationToEdit?.id) {
        await axios.put(buildApiUrl(`events/event-specifications/${specificationToEdit.id}/`), payload);
      } else {
        await axios.post(buildApiUrl('events/event-specifications/'), payload);
      }
      onSuccess();
    } catch (err) {
      console.error("שגיאה בשמירת המפרט:", err);
      setError("שגיאה בשמירת המפרט. אנא נסה שוב.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const addRentedItem = () => {
    setRentedItems([...rentedItems, {
      name: '',
      category_id: '',
      subcategory_id: '',
      supplier_name: '',
      arrival_date: '',
      departure_date: '',
      quantity: 1
    }]);
  };

  const removeRentedItem = (index: number) => {
    setRentedItems(rentedItems.filter((_, i) => i !== index));
  };

  const updateRentedItem = (index: number, field: keyof RentedItemData, value: any) => {
    const updated = [...rentedItems];
    updated[index] = { ...updated[index], [field]: value };
    setRentedItems(updated);
  };

  const getFilteredSubcategories = (categoryId: number | '') => {
    if (!categoryId) return [];
    return allSubcategories.filter(sub => sub.category === Number(categoryId));
  };

  // פונקציות לטיפול בחצים וקייסים (העתקה מ-EventEquipmentManager)
  const handleQuantityKeyDown = (e: React.KeyboardEvent, itemId: number | undefined, currentQuantity: number) => {
    if (itemId === undefined) return;
    
    // רק לחצים למעלה/למטה
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault(); // מונע את ההתנהגות הרגילה של השדה
    
      const inventoryItem = allEquipment.find(invEq => invEq.id === itemId);
      
      // קביעת גודל הקייס - ברירת מחדל 1
      const caseSize = (inventoryItem && inventoryItem.case_quantity > 1) ? inventoryItem.case_quantity : 1;
      let newQuantity = currentQuantity;
      
      // בדיקת מקסימום - רק אם יש הגבלה של מלאי זמין
      const maxAllowed = inventoryItem ? inventoryItem.quantity - inventoryItem.defective_quantity : 1000;
      
      if (e.key === 'ArrowUp') {
        newQuantity += caseSize;
        if (newQuantity > maxAllowed && maxAllowed > 0) {
          newQuantity = maxAllowed;
        }
      } else if (e.key === 'ArrowDown') {
        // אם אנחנו במקסימום שלא מתאים לקייס, אפס לקייס הקרוב
        if (currentQuantity === maxAllowed && maxAllowed > 0 && maxAllowed % caseSize !== 0) {
          newQuantity = Math.floor(maxAllowed / caseSize) * caseSize;
        } else {
          newQuantity = Math.max(0, newQuantity - caseSize);
        }
      }
      
      // עדכון הכמות
      setEquipmentQuantities({...equipmentQuantities, [itemId]: String(newQuantity)});
    }
  };

  const handleQuantityChange = (itemId: number | undefined, value: string) => {
    if (itemId === undefined) return;
    
    let requestedQuantity = Number(value);
    if (isNaN(requestedQuantity) || requestedQuantity < 0) requestedQuantity = 0;
    
    // זיהוי חצים רק אם השינוי הוא 1 בדיוק והערכים סבירים
    const currentQuantity = Number(equipmentQuantities[itemId] || 0);
    const difference = Math.abs(requestedQuantity - currentQuantity);
    
    // זיהוי חץ: השינוי הוא 1 בדיוק, והערכים סבירים (לא הקלדה ידנית)
    if (difference === 1 && requestedQuantity !== 0 && currentQuantity >= 0 && currentQuantity <= 1000) {
      const inventoryItem = allEquipment.find(invEq => invEq.id === itemId);
      if (inventoryItem && inventoryItem.case_quantity > 1) {
        const caseSize = inventoryItem.case_quantity;
        let newQuantity = currentQuantity;
        
        if (requestedQuantity > currentQuantity) {
          // חץ למעלה - הוסף קייס שלם
          newQuantity += caseSize;
          // בדיקת מקסימום
          const maxAllowed = inventoryItem.quantity - inventoryItem.defective_quantity;
          if (newQuantity > maxAllowed && maxAllowed > 0) {
            newQuantity = maxAllowed;
          }
        } else {
          // חץ למטה - הפחת קייס שלם
          // אם אנחנו במקסימום שלא מתאים לקייס, אפס לקייס הקרוב
          const maxAllowed = inventoryItem.quantity - inventoryItem.defective_quantity;
          if (currentQuantity === maxAllowed && maxAllowed > 0 && maxAllowed % caseSize !== 0) {
            newQuantity = Math.floor(maxAllowed / caseSize) * caseSize;
          } else {
            newQuantity = Math.max(0, currentQuantity - caseSize);
          }
        }
        
        
        // עדכון מיידי של ה-state
        setEquipmentQuantities({...equipmentQuantities, [itemId]: String(newQuantity)});
        return;
      }
    }

    const inventoryItem = allEquipment.find(invEq => invEq.id === itemId);

    // מקסימום מותר = המלאי הזמין הכולל
    const maxAllowed = inventoryItem ? inventoryItem.quantity - inventoryItem.defective_quantity : 1000;
    

    // הצמדה למדרגות קייס - תמיד עגל לקייס הקרוב
    let newQuantity = requestedQuantity;
    
    // אם יש כמות לקייס, עגל לקייס הקרוב (רק אם זה לא חץ)
    if (inventoryItem && inventoryItem.case_quantity > 1 && newQuantity > 0) {
        const caseSize = inventoryItem.case_quantity;
        
        // בדיקה אם זה חץ - אם השינוי הוא 1 בדיוק, זה חץ
        const difference = Math.abs(requestedQuantity - currentQuantity);
        
        // אם זה לא חץ (שינוי גדול מ-1), עגל לקייס הקרוב עם דיליי
        if (difference > 1) {
            // ביטול timeout קודם אם קיים
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
            
            // דיליי ארוך לאפשר לסיים להקליד 3 ספרות
            typingTimeoutRef.current = setTimeout(() => {
                let adjustedQuantity = requestedQuantity;
                
                // בדיקת מקסימום קודם
                const maxAllowed = inventoryItem ? inventoryItem.quantity - inventoryItem.defective_quantity : 1000;
                
                // אם הכמות גדולה מהמקסימום, תקן למקסימום
                if (adjustedQuantity > maxAllowed) {
                    adjustedQuantity = maxAllowed;
                }
                
                // עגל לקייס הקרוב רק אם לא הגענו למקסימום
                if (adjustedQuantity < maxAllowed) {
                    adjustedQuantity = Math.round(adjustedQuantity / caseSize) * caseSize;
                    
                    // אם התוצאה היא 0 אבל המשתמש רצה כמות חיובית, תן לו קייס אחד
                    if (adjustedQuantity === 0 && requestedQuantity > 0) {
                        adjustedQuantity = caseSize;
                    }
                }
                
                
                // עדכון הכמות
                setEquipmentQuantities({...equipmentQuantities, [itemId]: String(adjustedQuantity)});
            }, 1000); // דיליי של 1000ms (שנייה שלמה)
        }
    }

    // הגבלת טווח - לא יותר מהמלאי הזמין
    if (newQuantity > maxAllowed) {
        // תיקון אוטומטי למקסימום זמין - ללא הצמדה לקייס
        newQuantity = maxAllowed;
    }


    // עדכון מיידי של ה-state
    setEquipmentQuantities({...equipmentQuantities, [itemId]: String(newQuantity)});
  };

  // פונקציה לטיפול בלחיצה על פריט ציוד
  const handleEquipmentRowClick = (equipment: FullEquipment) => {
    setSelectedEquipmentForDetails(equipment);
    setShowEquipmentDetailModal(true);
  };

  if (loading) return <Spinner animation="border" />;

  return (
    <Form onSubmit={handleSubmit}>
      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      <Row>
        <Col md={8}>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-center mb-3">
                <Button size="sm" variant={addMode === 'equipment' ? 'primary' : 'outline-primary'} onClick={() => setAddMode('equipment')} className="me-2">
                  <FontAwesomeIcon icon={faCog} className="me-1" />
                  ציוד
                </Button>
                <Button size="sm" variant={addMode === 'consumable' ? 'success' : 'outline-success'} onClick={() => setAddMode('consumable')} className="me-2">
                  <FontAwesomeIcon icon={faBox} className="me-1" />
                  מתכלים
                </Button>
                <Button size="sm" variant={addMode === 'rented' ? 'info' : 'outline-info'} onClick={() => setAddMode('rented')}>
                  <FontAwesomeIcon icon={faTruck} className="me-1" />
                  ציוד מושכר
                </Button>
              </div>
            </Card.Header>
            <Card.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {addMode === 'equipment' && (
                <Accordion alwaysOpen>
                  {Object.entries(groupedInventory).map(([category, subcategories], catIndex) => (
                    <Accordion.Item eventKey={String(catIndex)} key={category}>
                      <Accordion.Header>{category}</Accordion.Header>
                      <Accordion.Body>
                        {Object.entries(subcategories).map(([subcategory, items]) => (
                          <div key={subcategory}>
                            <strong>{subcategory}</strong>
                            <Table striped borderless hover size="sm" responsive className="mt-1">
                              <tbody>
                                {items.map((item: FullEquipment) => (
                                  (() => {
                                    const qty = Number(equipmentQuantities[item.id!] || 0);
                                    const casesLabel = formatCasesLabel(qty, item.case_quantity);
                                    return (
                                  <tr key={item.id}>
                                    <td 
                                      onClick={() => handleEquipmentRowClick(item)}
                                      style={{ cursor: 'pointer' }}
                                      title="לחץ לצפייה בפרטי הציוד"
                                    >
                                      {item.name}
                                    </td>
                                    <td style={{ width: '80px' }}>
                                      <Form.Control 
                                        type="number"
                                        size="sm"
                                        placeholder="0"
                                        value={equipmentQuantities[item.id!] || ''}
                                        onChange={e => handleQuantityChange(item.id!, e.target.value)}
                                        onKeyDown={e => handleQuantityKeyDown(e, item.id!, Number(equipmentQuantities[item.id!] || 0))}
                                onKeyPress={e => {
                                  if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                    e.preventDefault();
                                    handleQuantityKeyDown(e as any, item.id!, Number(equipmentQuantities[item.id!] || 0));
                                  }
                                }}
                                        min={0}
                                      />
                                      {casesLabel && (
                                        <small className="d-block text-muted">
                                          ({casesLabel})
                                        </small>
                                      )}
                                    </td>
                                  </tr>
                                    );
                                  })()
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        ))}
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              )}
              {addMode === 'consumable' && (
                <Table striped borderless hover size="sm" responsive>
                  <tbody>
                    {allConsumables.map((item: Consumable) => (
                      <tr key={item.id}>
                        <td>{item.name} <small className="text-muted">({item.unit})</small></td>
                        <td style={{ width: '80px' }}>
                          <Form.Control 
                            type="number"
                            size="sm"
                            placeholder="0"
                            value={consumableQuantities[item.id!] || ''}
                            onChange={e => setConsumableQuantities({...consumableQuantities, [item.id!]: e.target.value})}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
              {addMode === 'rented' && (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6>ציוד מושכר</h6>
                    <Button size="sm" variant="outline-primary" onClick={addRentedItem}>
                      הוסף פריט מושכר
                    </Button>
                  </div>
                  {rentedItems.map((item, index) => (
                    <Card key={index} className="mb-3">
                      <Card.Body>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-2">
                              <Form.Label>שם הפריט *</Form.Label>
                              <Form.Control
                                type="text"
                                value={item.name}
                                onChange={(e) => updateRentedItem(index, 'name', e.target.value)}
                                required
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-2">
                              <Form.Label>ספק *</Form.Label>
                              <Form.Control
                                type="text"
                                value={item.supplier_name}
                                onChange={(e) => updateRentedItem(index, 'supplier_name', e.target.value)}
                                required
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={4}>
                            <Form.Group className="mb-2">
                              <Form.Label>קטגוריה *</Form.Label>
                              <Form.Select
                                value={item.category_id}
                                onChange={(e) => {
                                  updateRentedItem(index, 'category_id', e.target.value);
                                  updateRentedItem(index, 'subcategory_id', '');
                                }}
                                required
                              >
                                <option value="">בחר...</option>
                                {allCategories.map(cat => (
                                  <option key={cat.id} value={cat.id}>{cat.category_name}</option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-2">
                              <Form.Label>תת-קטגוריה</Form.Label>
                              <Form.Select
                                value={item.subcategory_id}
                                onChange={(e) => updateRentedItem(index, 'subcategory_id', e.target.value)}
                                disabled={!item.category_id}
                              >
                                <option value="">בחר...</option>
                                {getFilteredSubcategories(item.category_id).map(sub => (
                                  <option key={sub.id} value={sub.id}>{sub.subcategory_name}</option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-2">
                              <Form.Label>כמות</Form.Label>
                              <Form.Control
                                type="number"
                                value={item.quantity}
                                onChange={(e) => updateRentedItem(index, 'quantity', parseInt(e.target.value) || 1)}
                                min="1"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-2">
                              <Form.Label>תאריך הגעה</Form.Label>
                              <Form.Control
                                type="date"
                                value={item.arrival_date}
                                onChange={(e) => updateRentedItem(index, 'arrival_date', e.target.value)}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-2">
                              <Form.Label>תאריך עזיבה</Form.Label>
                              <Form.Control
                                type="date"
                                value={item.departure_date}
                                onChange={(e) => updateRentedItem(index, 'departure_date', e.target.value)}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        <div className="text-end">
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => removeRentedItem(index)}
                          >
                            הסר פריט
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                  {rentedItems.length === 0 && (
                    <Alert variant="info">
                      לא נוספו פריטים מושכרים למפרט זה. לחץ על "הוסף פריט מושכר" כדי להתחיל.
                    </Alert>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Header>פרטי המפרט</Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>שם המפרט *</Form.Label>
                <Form.Control
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>תיאור</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Form.Group>
              <div className="d-grid gap-2">
                <Button type="submit" variant="success" disabled={submitLoading}>
                  {submitLoading ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faSave} />}
                  {specificationToEdit ? 'עדכן מפרט' : 'צור מפרט'}
                </Button>
                <Button variant="secondary" onClick={onCancel}>
                  <FontAwesomeIcon icon={faTimes} />
                  ביטול
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* מודל פרטי ציוד */}
      <EquipmentDetailModal 
        show={showEquipmentDetailModal} 
        onHide={() => setShowEquipmentDetailModal(false)} 
        equipment={selectedEquipmentForDetails} 
        allEquipment={allEquipment} 
      />
    </Form>
  );
};

export default EventSpecificationForm;
